import React, { useState, useEffect } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Settings {
  kpis: string[];
  thresholds: {
    [key: string]: {
      green: number;
      yellow: number;
    };
  };
  currency: string;
}

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<Settings>({
    kpis: ['ggr', 'ngr', 'turnover', 'deposits', 'withdrawals', 'activePlayers'],
    thresholds: {
      ggr: { green: 5, yellow: 2 },
      ngr: { green: 5, yellow: 2 },
      turnover: { green: 10, yellow: 5 },
      deposits: { green: 5, yellow: 2 },
      withdrawals: { green: 5, yellow: 2 },
      activePlayers: { green: 5, yellow: 2 },
    },
    currency: 'EUR',
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('dashboard_settings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (err) {
      setError(t('settings.failedToLoadSettings'));
    }
  };

  const handleKpiChange = (kpi: string, checked: boolean) => {
    setSettings({
      ...settings,
      kpis: checked
        ? [...settings.kpis, kpi]
        : settings.kpis.filter((k) => k !== kpi),
    });
  };

  const handleThresholdChange = (
    kpi: string,
    type: 'green' | 'yellow',
    value: number
  ) => {
    setSettings({
      ...settings,
      thresholds: {
        ...settings.thresholds,
        [kpi]: {
          ...settings.thresholds[kpi],
          [type]: value,
        },
      },
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSaved(false);

    try {
      localStorage.setItem('dashboard_settings', JSON.stringify(settings));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(t('settings.failedToSaveSettings'));
    } finally {
      setLoading(false);
    }
  };

  const allKpis = [
    { id: 'ggr', label: 'Gross Gaming Revenue' },
    { id: 'ngr', label: 'Net Gaming Revenue' },
    { id: 'turnover', label: 'Total Turnover' },
    { id: 'deposits', label: 'Deposits' },
    { id: 'withdrawals', label: 'Withdrawals' },
    { id: 'activePlayers', label: 'Active Players' },
  ];

  const currencies = ['EUR', 'GBP', 'USD', 'SEK', 'NOK', 'DKK'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {t('settings.title')}
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {error}
          </p>
        </div>
      )}

      {saved && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900/50 dark:bg-green-900/20">
          <p className="text-sm font-medium text-green-600 dark:text-green-400">
            {t('settings.settingsSavedSuccessfully')}
          </p>
        </div>
      )}

      {/* Settings Form */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* KPI Selection */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-bold text-slate-900 dark:text-white">
            {t('settings.dashboardKpis')}
          </h2>
          <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">
            {t('settings.dashboardKpisDescription')}
          </p>

          <div className="space-y-3">
            {allKpis.map((kpi) => (
              <label
                key={kpi.id}
                className="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <input
                  type="checkbox"
                  checked={settings.kpis.includes(kpi.id)}
                  onChange={(e) => handleKpiChange(kpi.id, e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span className="font-medium text-slate-900 dark:text-white">
                  {kpi.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Currency Selection */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-bold text-slate-900 dark:text-white">
            {t('settings.displaySettings')}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-900 dark:text-white">
                {t('settings.defaultCurrency')}
              </label>
              <select
                value={settings.currency}
                onChange={(e) =>
                  setSettings({ ...settings, currency: e.target.value })
                }
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              >
                {currencies.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Threshold Configuration */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-bold text-slate-900 dark:text-white">
          {t('settings.alertThresholds')}
        </h2>
        <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">
          {t('settings.alertThresholdsDescription')}
        </p>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {allKpis.map((kpi) => (
            <div
              key={kpi.id}
              className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-700/50"
            >
              <h3 className="mb-4 font-medium text-slate-900 dark:text-white">
                {kpi.label}
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.greenThreshold')}
                  </label>
                  <input
                    type="number"
                    value={settings.thresholds[kpi.id]?.green || 5}
                    onChange={(e) =>
                      handleThresholdChange(
                        kpi.id,
                        'green',
                        parseFloat(e.target.value)
                      )
                    }
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.yellowThreshold')}
                  </label>
                  <input
                    type="number"
                    value={settings.thresholds[kpi.id]?.yellow || 2}
                    onChange={(e) =>
                      handleThresholdChange(
                        kpi.id,
                        'yellow',
                        parseFloat(e.target.value)
                      )
                    }
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg bg-accent px-6 py-2 font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {loading ? t('settings.saving') : t('settings.saveSettings')}
        </button>
      </div>

      {/* Info Box */}
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 dark:border-accent-900/50 dark:bg-accent-900/20">
        <p className="text-sm text-accent-800 dark:text-accent-200">
          {t('settings.infoText')}
        </p>
      </div>
    </div>
  );
};
